import { StoreState } from '@/core/models/store.interface';
import { candidateMapper } from '@/modules/candidates/mappers/candidate.mapper';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
import { FilterInfoType } from '../filter-info/filter-info.interface';
import { Filters } from '../filters/filter.interface';
import { ApiResource } from './../../models/api-resource.interface';
import activity, { ActivityState } from './activities/activity.store';
import call, { CallState } from './calls/call.store';
import { Candidate } from './candidate.interface';
import { CANDIDATE_INCLUDES, CandidateService } from './candidate.service';
import education, { EducationState } from './educations/education.store';
import exists, { ExistsState } from './exists/exists.store';
import candidateJourney, { JourneyState } from './journeys/candidate-journey.store';
import notes, { CandidateNoteState } from './notes/note.store';
import resume, { ResumeState } from './resume/resume.store';
import resumeUpload, { ResumeUploadState } from './resume/resume-upload.store';
import publicResume, { PublicResumeState } from './resumes/resume.store';
import sms, { SmsState } from './sms/sms.store';

const service = new CandidateService();

export interface CandidateState {
   candidatesOverview?: ApiResource<Array<Candidate>>;
   filters?: Filters;
   filtersByTargetType?: Partial<Record<FilterInfoType, Filters>>;
   query?: LocationQuery;
   activeIndex: number;
   updateActivities: boolean;
   candidate?: Candidate;

   //modules
   call?: CallState;
   sms?: SmsState;
   resume?: ResumeState;
   resumeUpload?: ResumeUploadState;
   publicResume?: PublicResumeState;
   activity?: ActivityState;
   exists?: ExistsState;
   journey?: JourneyState;
   notes?: CandidateNoteState;
   education?: EducationState;
}

const CandidateStore: Module<CandidateState, StoreState> = {
   namespaced: true,
   state: {
      filters: { quick_search: [], filter_elements: [] },
      activeIndex: 0,
      updateActivities: false,
      candidate: {} as Candidate,
   },
   getters: {
      candidatesOverview: (state) => state.candidatesOverview,
      filters: (state) => state.filters,
      query: (state) => state.query,
      activeIndex: (state) => state.activeIndex,
   },
   mutations: {
      SET_CANDIDATES_OVERVIEW(state, candidatesOverview?: ApiResource<Array<Candidate>>) {
         state.candidatesOverview = candidatesOverview;
      },
      SET_QUERY(state, query: LocationQuery) {
         state.query = query;
      },
      UPDATE_CANDIDATE_LIST(state, candidate: Candidate) {
         if (state.candidatesOverview) {
            const oldCandidate = state.candidatesOverview.data.find((c) => c.id === candidate.id);
            const index = state.candidatesOverview!.data.findIndex((c) => c.id === candidate.id);
            if (state.candidatesOverview?.data[index]) {
               state.candidatesOverview?.data.splice(
                  index,
                  1,
                  candidateMapper({ ...candidate, elastic_score: oldCandidate?.elastic_score || 0 } as Candidate),
               );
               state.activeIndex = index;
            }
         }
      },
      SET_UPDATE_ACTIVITIES(state, updateActivities: boolean) {
         state.updateActivities = updateActivities;
      },
      DELETE_FROM_CANDIDATE_LIST(state, positionIds) {
         state.candidatesOverview!.data = state.candidatesOverview?.data.filter((c) => !positionIds.includes(c.id)) || [];
      },
      UPDATE_CANDIDATE(state, candidate: Candidate) {
         if (!state.candidatesOverview) return;
         const index = state.candidatesOverview!.data.findIndex((c) => c.id === candidate.id);
         if (index > -1) {
            const candidateFromServer = candidateMapper(candidate);
            const updatedCandidate = deepMergeObject(state.candidatesOverview!.data[index], candidateFromServer) as Candidate;

            state.candidatesOverview.data.splice(index, 1, candidateMapper(updatedCandidate));
            state.activeIndex = index;
         }
      },
      SET_CANDIDATE(state, candidate: Candidate) {
         state.candidate = candidate;
      },
   },
   actions: {
      async getFilteredCandidates(context, { query, filters }: { query: LocationQuery; filters: Filters }) {
         const candidatesOverview = await service.getFilteredCandidates(query, filters);
         return candidatesOverview;
      },
      async getCandidatesDirect(context, query: LocationQuery) {
         const response = await service.getCandidatesDirect(query);
         return response;
      },
      async searchCandidates(context, query: LocationQuery) {
         const response = await service.getCandidates({
            per_page: '99',
            page: '1',
            include: CANDIDATE_INCLUDES.dropdown.includes.join(','),
            append: CANDIDATE_INCLUDES.dropdown.appends.join(','),
            ...query,
         });
         return response.data;
      },
      async autocomplete(context, search: string) {
         return await service.autocomplete(search);
      },
      async createCandidate(context, candidate: Candidate) {
         const response = await service.postCandidate(candidate);
         return response;
      },
      async getCandidate(context, payload: { candidateId: number; query: LocationQuery }) {
         const response = await service.getCandidate(payload.candidateId, payload.query);
         context.commit('SET_CANDIDATE', response);
         return response;
      },
      async putCandidate(context, candidate: Candidate) {
         const response = await service.putCandidate(candidate.id, candidate);
         context.commit('UPDATE_CANDIDATE_LIST', { ...response, elastic_score: candidate.elastic_score } as Candidate);
         return response;
      },
      async exportCandidates(context, { query, filters }: { query: LocationQuery; filters: Filters }) {
         const response = await service.exportCandidates(query, filters);
         return response;
      },
      async searchJourney(context, payload: { filters: Filters; query: LocationQuery }) {
         const response = await service.searchJourney(payload.filters, payload.query);
         return response;
      },
      async searchCandidateJourney(context, payload: { filters: Filters; query: LocationQuery }) {
         const response = await service.searchCandidateJourney(payload.filters, payload.query);
         return response;
      },
      async checkDelete(context, payload: { candidateId: number; force: boolean }) {
         const response = await service.checkDelete(payload.candidateId, payload.force);
         return response;
      },
      async deleteCandidate(context, payload: { candidateId: number; force: boolean }) {
         const response = await service.deleteCandidate(payload.candidateId, payload.force);
         return response;
      },
   },
   modules: {
      call,
      sms,
      resume,
      resumeUpload,
      publicResume,
      activity,
      exists,
      journey: candidateJourney,
      notes,
      education,
   },
};

export default CandidateStore;
